import { useState } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router';
import { Button, Alert, InputOTP, Logo } from '@heroui/react';
import { useAuthStore } from '../../stores/authStore';
import { safeNextPath } from '../../lib/nextPath';
import { useUiStore } from '../../stores/uiStore';
import { useTrack } from '../../telemetry';

export default function MfaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Phase 49 — preserve the pre-login destination through MFA.
  const nextPath = safeNextPath(searchParams.get('next'));
  const mfaSessionToken = useAuthStore((s) => s.mfaSessionToken);
  const validateMfa = useAuthStore((s) => s.validateMfa);
  const clearMfaToken = useAuthStore((s) => s.clearMfaToken);
  const theme = useUiStore((s) => s.resolvedTheme);
  const track = useTrack();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If no MFA session token, redirect to login
  if (!mfaSessionToken) {
    return <Navigate to="/login" replace />;
  }

  const handleComplete = async (value: string) => {
    setError(null);
    setIsSubmitting(true);

    try {
      await validateMfa(value);
      track('dashboard.auth.mfa_submit', { context: { result: 'success' } });
      navigate(nextPath);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(message || 'Invalid verification code');
      setCode('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    clearMfaToken();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-default-50">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center space-y-3">
          <Logo variant="stacked" size={120} onDark={theme === 'dark'} />
          <div className="text-center space-y-1">
            <h1 className="text-xl font-semibold">Verification Required</h1>
            <p className="text-default-500 text-sm">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>
        </div>

        {error && (
          <Alert status="danger">
            {error}
          </Alert>
        )}

        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            onComplete={handleComplete}
            isDisabled={isSubmitting}
          >
            <InputOTP.Group>
              <InputOTP.Slot index={0} />
              <InputOTP.Slot index={1} />
              <InputOTP.Slot index={2} />
            </InputOTP.Group>
            <InputOTP.Separator />
            <InputOTP.Group>
              <InputOTP.Slot index={3} />
              <InputOTP.Slot index={4} />
              <InputOTP.Slot index={5} />
            </InputOTP.Group>
          </InputOTP>
        </div>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="flex-1"
            onPress={handleBack}
          >
            Back to Login
          </Button>
          <Button
            className="flex-1"
            isDisabled={code.length < 6 || isSubmitting}
            onPress={() => handleComplete(code)}
          >
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </Button>
        </div>
      </div>
    </div>
  );
}
